/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export function usePages(appId) {
  return useQuery({
    queryKey: ['pages', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/${appId}/pages/`,
        { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error('Failed to load pages');
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 0,
  });
}

export function usePage(pageId) {
  return useQuery({
    queryKey: ['page', pageId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/page/${pageId}/`,
        { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error('Failed to load page');
      return resp.json();
    },
    enabled: !!pageId,
    staleTime: 0,
  });
}

export function useHomepageType(appId) {
  return useQuery({
    queryKey: ['homepageType', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/homepage-type/?application_id=${appId}`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { homepage_type: 'default' };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 0,
  });
}

export async function setHomepageType(appId, homepageType) {
  const resp = await apiHelper.apiCall('/arcee/application/homepage-type/', {
    method: 'POST',
    body: JSON.stringify({ application_id: parseInt(appId), homepage_type: homepageType }),
  });
  if (!resp.ok) throw new Error('Failed to update homepage type');
  return resp.json();
}

export async function createPage(appId, data) {
  const resp = await apiHelper.apiCall('/arcee/application/page/create/', {
    method: 'POST',
    body: JSON.stringify({ application_id: parseInt(appId), ...data }),
  });
  if (!resp.ok) {
    let body = {};
    try { body = await resp.json(); } catch (_) {}
    throw new Error(body.detail || 'Failed to create page');
  }
  return resp.json();
}

export async function getPage(pageId) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/`, { method: 'GET' });
  if (!resp.ok) throw new Error('Failed to load page');
  return resp.json();
}

export async function updatePage(pageId, data) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/update/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error('Failed to update page');
  return resp.json();
}

export async function deletePage(pageId) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/delete/`, {
    method: 'DELETE',
  });
  if (!resp.ok) throw new Error('Failed to delete page');
  return resp.json();
}

export async function setPageVisibility(pageId, visibility) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/update/`, {
    method: 'PUT',
    body: JSON.stringify({ visibility }),
  });
  if (!resp.ok) throw new Error('Failed to update page visibility');
  return resp.json();
}

/**
 * Adds a page to the app, or takes it back out.
 *
 * Separate from publishing on purpose: published means the page is finished,
 * in-app means people can browse to it at /app/<slug>. Pass nothing to flip it.
 */
export async function setPageInApp(pageId, inApp) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/app-visibility/`, {
    method: 'POST',
    body: JSON.stringify(inApp === undefined ? {} : { in_app: inApp }),
  });
  if (!resp.ok) throw new Error('Failed to change whether this page is in the app');
  return resp.json();
}

export async function publishPage(pageId) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/publish/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!resp.ok) throw new Error('Failed to publish page');
  return resp.json();
}

/**
 * Fetch a page's portable config document — sections, styling, markup and form
 * definitions, with every database row number removed or translated so the file
 * can be seeded elsewhere. Returns the parsed document; the caller saves it.
 */
export async function exportPage(pageId) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/export/`, {
    method: 'GET',
  });
  if (!resp.ok) throw new Error('Failed to export page');
  return resp.json();
}

/**
 * Seed a downloaded page export into an application.
 *
 * With `preview` true nothing is written — the server reports what *would*
 * happen (the slug the page lands under, tables it needs that are missing) so
 * the admin decides before anything is created.
 */
export async function importPage(appId, file, { preview = false } = {}) {
  const body = new FormData();
  body.append('file', file);
  if (preview) body.append('preview', 'true');

  const resp = await apiHelper.apiCall(`/arcee/application/${appId}/page/import/`, {
    method: 'POST',
    body,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data?.detail || 'Failed to import page');
  }
  return data;
}

export async function saveSections(pageId, sections) {
  const resp = await apiHelper.apiCall(`/arcee/application/page/${pageId}/sections/save/`, {
    method: 'POST',
    body: JSON.stringify({ sections }),
  });
  if (!resp.ok) throw new Error('Failed to save sections');
  return resp.json();
}

export function useSectionTemplates(appId) {
  return useQuery({
    queryKey: ['sectionTemplates', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/section-templates/?application_id=${appId}`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return [];
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });
}

export async function savePageTemplate(appId, name, sections) {
  const resp = await apiHelper.apiCall('/arcee/application/section-template/save/', {
    method: 'POST',
    body: JSON.stringify({
      application_id: parseInt(appId),
      name,
      section_type: 'page_template',
      default_config: {
        sections: sections.map(s => ({
          section_type:      s.section_type,
          section_config:    s.section_config,
          layout_config:     s.layout_config,
          data_binding:      s.data_binding,
          is_visible:        s.is_visible,
          responsive_config: s.responsive_config,
          order:             s.order,
        })),
      },
    }),
  });
  if (!resp.ok) throw new Error('Failed to save page template');
  return resp.json();
}

export async function deleteTemplate(templateId) {
  const resp = await apiHelper.apiCall(
    `/arcee/application/section-template/${templateId}/delete/`,
    { method: 'DELETE' }
  );
  if (!resp.ok) throw new Error('Failed to delete template');
  return resp.json();
}

export function useFormSubmissions(formId, params = {}) {
  const { page = 1, pageSize = 25, search = '', dateFrom = '', dateTo = '', status = 'submitted' } = params;
  return useQuery({
    queryKey: ['formSubmissions', formId, page, pageSize, search, dateFrom, dateTo, status],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({
        page, page_size: pageSize,
        ...(search && { search }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        ...(status !== 'all' && { status }),
      });
      const resp = await apiHelper.apiCall(
        `/arcee/application/form/${formId}/submissions/?${qs}`,
        { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error('Failed to load submissions');
      return resp.json();
    },
    enabled: !!formId,
    staleTime: 0,
  });
}

export async function deleteSubmission(formId, submissionId) {
  const resp = await apiHelper.apiCall(
    `/arcee/application/form/${formId}/submission/${submissionId}/`,
    { method: 'DELETE' }
  );
  if (!resp.ok) throw new Error('Failed to delete submission');
  return resp.json();
}


export function getExportUrl(formId) {
  return `${envConfig.backendApiUrl}/arcee/application/form/${formId}/submissions/export/`;
}

export { apiHelper };
